import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * MarkdownRenderer Component
 *
 * Renders markdown content with custom styling for dark theme.
 * Supports:
 * - Headers (H1-H6)
 * - Bold/italic text
 * - Lists (ordered/unordered)
 * - Horizontal rules
 * - Emojis
 * - Code blocks
 * - Tables
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className }) => {
  return (
    <div className={`markdown-content ${className || ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // Headers
          h1: ({ node, ...props }) => (
            <h1
              style={{
                color: '#ECECEC',
                fontSize: '28px',
                fontWeight: '600',
                marginTop: '32px',
                marginBottom: '16px',
                lineHeight: '1.4',
                letterSpacing: '-0.5px',
              }}
              {...props}
            />
          ),
          h2: ({ node, ...props }) => (
            <h2
              style={{
                color: '#ECECEC',
                fontSize: '24px',
                fontWeight: '600',
                marginTop: '28px',
                marginBottom: '14px',
                lineHeight: '1.4',
                letterSpacing: '-0.4px',
              }}
              {...props}
            />
          ),
          h3: ({ node, ...props }) => (
            <h3
              style={{
                color: '#ECECEC',
                fontSize: '20px',
                fontWeight: '600',
                marginTop: '24px',
                marginBottom: '12px',
                lineHeight: '1.5',
                letterSpacing: '-0.3px',
              }}
              {...props}
            />
          ),
          h4: ({ node, ...props }) => (
            <h4
              style={{
                color: '#ECECEC',
                fontSize: '18px',
                fontWeight: '600',
                marginTop: '20px',
                marginBottom: '10px',
                lineHeight: '1.5',
              }}
              {...props}
            />
          ),
          h5: ({ node, ...props }) => (
            <h5
              style={{
                color: '#ECECEC',
                fontSize: '16px',
                fontWeight: '600',
                marginTop: '18px',
                marginBottom: '8px',
                lineHeight: '1.5',
              }}
              {...props}
            />
          ),
          h6: ({ node, ...props }) => (
            <h6
              style={{
                color: '#C5C5C5',
                fontSize: '15px',
                fontWeight: '600',
                marginTop: '16px',
                marginBottom: '8px',
                lineHeight: '1.5',
              }}
              {...props}
            />
          ),

          // Paragraphs
          p: ({ node, ...props }) => (
            <p
              style={{
                color: '#ECECEC',
                fontSize: '16px',
                lineHeight: '1.8',
                marginBottom: '16px',
                marginTop: '0',
              }}
              {...props}
            />
          ),

          // Strong (Bold)
          strong: ({ node, ...props }) => (
            <strong
              style={{
                color: '#ECECEC',
                fontWeight: '600',
              }}
              {...props}
            />
          ),

          // Emphasis (Italic)
          em: ({ node, ...props }) => (
            <em
              style={{
                color: '#ECECEC',
                fontStyle: 'italic',
              }}
              {...props}
            />
          ),

          // Lists
          ul: ({ node, ...props }) => (
            <ul
              style={{
                color: '#ECECEC',
                paddingLeft: '28px',
                marginTop: '12px',
                marginBottom: '16px',
                listStyleType: 'disc',
              }}
              {...props}
            />
          ),
          ol: ({ node, ...props }) => (
            <ol
              style={{
                color: '#ECECEC',
                paddingLeft: '28px',
                marginTop: '12px',
                marginBottom: '16px',
              }}
              {...props}
            />
          ),
          li: ({ node, ...props }) => (
            <li
              style={{
                color: '#ECECEC',
                marginBottom: '8px',
                lineHeight: '1.7',
                fontSize: '16px',
              }}
              {...props}
            />
          ),

          // Horizontal Rule
          hr: ({ node, ...props }) => (
            <hr
              style={{
                border: 'none',
                borderTop: '1px solid #2c2f2f',
                margin: '20px 0',
              }}
              {...props}
            />
          ),

          // Code blocks
          code: ({ node, inline, ...props }: any) => {
            if (inline) {
              return (
                <code
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#ECECEC',
                    padding: '3px 8px',
                    borderRadius: '6px',
                    fontSize: '15px',
                    fontFamily: '"SF Mono", Monaco, Consolas, "Courier New", monospace',
                  }}
                  {...props}
                />
              );
            }
            return (
              <code
                style={{
                  display: 'block',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: '#ECECEC',
                  padding: '16px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontFamily: '"SF Mono", Monaco, Consolas, "Courier New", monospace',
                  overflowX: 'auto',
                  marginTop: '12px',
                  marginBottom: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  lineHeight: '1.6',
                }}
                {...props}
              />
            );
          },
          pre: ({ node, ...props }) => (
            <pre
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '0',
                borderRadius: '10px',
                overflowX: 'auto',
                marginTop: '12px',
                marginBottom: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
              {...props}
            />
          ),

          // Blockquote
          blockquote: ({ node, ...props }) => (
            <blockquote
              style={{
                borderLeft: '3px solid rgba(255, 255, 255, 0.2)',
                paddingLeft: '20px',
                marginLeft: '0',
                marginTop: '16px',
                marginBottom: '16px',
                color: '#C5C5C5',
                fontStyle: 'normal',
                fontSize: '16px',
                lineHeight: '1.7',
              }}
              {...props}
            />
          ),

          // Tables
          table: ({ node, ...props }) => (
            <div style={{ overflowX: 'auto', marginTop: '12px', marginBottom: '16px' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: '#1f2121',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #2c2f2f',
                }}
                {...props}
              />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead
              style={{
                background: '#2c2f2f',
              }}
              {...props}
            />
          ),
          th: ({ node, ...props }) => (
            <th
              style={{
                color: '#FFFFFF',
                padding: '12px',
                textAlign: 'left',
                fontWeight: '600',
                borderBottom: '2px solid #3A3A38',
              }}
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td
              style={{
                color: '#FFFFFF',
                padding: '12px',
                borderBottom: '1px solid #2c2f2f',
              }}
              {...props}
            />
          ),

          // Links
          a: ({ node, ...props }) => (
            <a
              style={{
                color: '#3b82f6',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
